import { useCallback, useEffect, useLayoutEffect } from "react";
import { CircleCheckIcon, TriangleAlertIcon } from "lucide-react";
import { StripeIcon } from "@/objects/@icons";
import { create } from "zustand";

import { EntityFragment } from "@/__generated__/graphql";
import { Buttons } from "@/components/atoms/Buttons";
import { useRestApi } from "@/hooks/useRestApi";
import { useScope } from "@/hooks/useScope";
import { State } from "@/state";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Typography } from "@/components/ui/typography";
import { COMMAND_ID as SELECT_ENTITY_COMMAND_ID } from "./SelectEntityCommand";

export const COMMAND_ID = "connect-stripe-account";

export type ConnectStripeAccountContextType = Record<string, never>;

// Integration state types
type IntegrationConnectionState = "idle" | "connecting" | "success" | "error";

type IntegrationState = {
  state: IntegrationConnectionState;
  message: string | null;
};

type IntegrationStore = IntegrationState & {
  setState: (
    state: IntegrationConnectionState,
    message?: string | null,
  ) => void;
  reset: () => void;
};

// Local integration store
const useIntegrationStore = create<IntegrationStore>((set) => ({
  state: "idle",
  message: null,
  setState: (state: IntegrationConnectionState, message?: string | null) =>
    set({ state, message: message || null }),
  reset: () => set({ state: "idle", message: null }),
}));

// Custom hook for OAuth popup handling
function useOAuthPopup() {
  const openPopup = useCallback((url: string) => {
    return new Promise<{ success: boolean; message?: string }>((resolve) => {
      const popup = window.open(
        url,
        "oauth-popup",
        "width=600,height=700,scrollbars=yes,resizable=yes",
      );

      if (!popup) {
        resolve({
          success: false,
          message: "Failed to open popup. Please check if popups are blocked.",
        });
        return;
      }

      // Listen for messages from the popup (integrations page)
      const messageHandler = (event: MessageEvent) => {
        // Only accept messages from the same origin for security
        if (event.origin !== window.location.origin) return;

        const { integration, status, message } = event.data;

        if (integration && status) {
          cleanup();

          if (status === "success") {
            resolve({ success: true, message });
          } else {
            resolve({
              success: false,
              message: message || "Integration failed",
            });
          }
        }
      };

      // Check if popup is closed manually
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          cleanup();
          resolve({ success: false, message: "Connection cancelled" });
        }
      }, 1000);

      // Cleanup function
      const cleanup = () => {
        window.removeEventListener("message", messageHandler);
        clearInterval(checkClosed);
        if (!popup.closed) {
          popup.close();
        }
      };

      // Listen for messages
      window.addEventListener("message", messageHandler);
    });
  }, []);

  return { openPopup };
}

function Component({ commandInstanceId }: { commandInstanceId: string }) {
  const { state, message } = useIntegrationStore();
  return (
    <div className="p-4 flex flex-col text-left w-full gap-2">
      {state === "idle" && (
        <>
          <Typography level="body-md">
            Connect your Stripe account to automatically sync your payment data
            and reconcile transactions in your accounting system.
            <br />
            <br />
            This will allow NeoLedger to:
          </Typography>
          <ul style={{ margin: 0 }}>
            <li>
              <Typography level="body-md">
                Read your charges, refunds, and payout data
              </Typography>
            </li>
            <li>
              <Typography level="body-md">
                Automatically create accounting transactions
              </Typography>
            </li>
            <li>
              <Typography level="body-md">
                Reconcile payments with invoices
              </Typography>
            </li>
          </ul>
        </>
      )}

      {state === "connecting" && (
        <>
          <Typography level="body-md">
            Please complete the authorization in the Stripe popup window.
          </Typography>
        </>
      )}

      {state === "success" && (
        <>
          <Alert>
            <CircleCheckIcon size="1em" />
            <AlertTitle>Successfully Connected!</AlertTitle>
            <AlertDescription>
              Your Stripe account has been connected. You can now sync your
              transaction data.
            </AlertDescription>
          </Alert>
        </>
      )}

      {state === "error" && (
        <>
          <Alert variant="destructive">
            <TriangleAlertIcon size="1em" />
            <AlertTitle>Connection Failed!</AlertTitle>
            <AlertDescription>{message}</AlertDescription>
          </Alert>
        </>
      )}
    </div>
  );
}

function Footer({ commandInstanceId }: { commandInstanceId: string }) {
  const unregisterCommandDefinition = State(
    (state) => state.unregisterCommandDefinition,
  );
  const clearCommandStack = State((state) => state.clearCommandStack);
  const { state, setState, reset } = useIntegrationStore();
  const { activeEntityIdentifier, isLoading } = useScope();
  const { get } = useRestApi();
  const { openPopup } = useOAuthPopup();

  useEffect(() => {
    reset();
  }, [reset]);

  const handleConnect = useCallback(async () => {
    if (isLoading || !activeEntityIdentifier) return;

    setState("connecting", null);

    try {
      const response = await get(
        `/v1/integrations/stripe/oauth/connect-url?entity_identifier=${activeEntityIdentifier}`,
      );

      // Open popup and wait for the result from the integrations page
      const result = await openPopup(response.url);

      if (result.success) {
        setState("success", result.message);
      } else {
        setState("error", result.message || "Failed to connect to Stripe");
      }
    } catch (error) {
      setState(
        "error",
        error instanceof Error ? error.message : "Failed to connect to Stripe",
      );
    }
  }, [activeEntityIdentifier, isLoading, get, openPopup, setState]);

  const buttons = [];

  if (state === "idle") {
    buttons.push({
      variant: "default",
      children: "Connect Stripe Account",

      onClick: () => {
        handleConnect();
      },
    });
  }

  if (state === "connecting") {
    buttons.push({
      variant: "outline",
      children: "Cancel",

      onClick: () => {
        unregisterCommandDefinition("connect-stripe-account");
      },
    });
  }

  if (state === "success") {
    buttons.push({
      variant: "outline",
      children: "Dismiss",

      onClick: () => {
        unregisterCommandDefinition("connect-stripe-account");
        clearCommandStack();
      },
    });
  }

  if (state === "error") {
    buttons.push({
      variant: "outline",
      children: "Try Again",

      onClick: () => {
        handleConnect();
        setState("idle", null);
        clearCommandStack();
      },
    });
  }

  return <Buttons buttons={buttons} />;
}

export function ConnectStripeAccountCommand() {
  const registerCommandDefinition = State(
    (state) => state.registerCommandDefinition,
  );
  const unregisterCommandDefinition = State(
    (state) => state.unregisterCommandDefinition,
  );
  const updateCommandDefinition = State(
    (state) => state.updateCommandDefinition,
  );

  const { activeEntities, isLoading } = useScope();

  // Register eagerly as visible — hide later only if all entities already have Stripe connected
  useLayoutEffect(() => {
    registerCommandDefinition({
      id: COMMAND_ID,
      title: "Connect Stripe Account",
      getLabel: () => "Connect Stripe Account",
      description: "Connect your Stripe account for payment processing",
      component: Component,
      footer: Footer,
      ReactIcon: StripeIcon,
      onClick: () => {
        const entities = State.getState().entities;
        State.getState().pushToCommandStack({
          commandDefinitionId: SELECT_ENTITY_COMMAND_ID,
          context: {
            entityIdentifiers: entities
              .filter((entity) => !entity?.integrations?.stripe?.isActive)
              .map((entity) => entity.identifier),
            onSubmit: (entity: EntityFragment) => {
              State.getState().pushToCommandStack({
                commandDefinitionId: COMMAND_ID,
                context: {
                  entityIdentifier: entity.identifier,
                },
              });
            },
          },
        });
      },
      group: "Integrations",
    });

    return () => {
      unregisterCommandDefinition(COMMAND_ID);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hide command only if data is loaded AND all entities have Stripe connected
  useEffect(() => {
    if (isLoading) return;
    // Don't hide when entities haven't been populated yet
    if (!activeEntities || activeEntities.length === 0) return;

    const isAnyEntityUnconnected = activeEntities.some(
      (entity) => !entity?.integrations?.stripe?.isActive,
    );
    if (!isAnyEntityUnconnected) {
      updateCommandDefinition(COMMAND_ID, { isHidden: true });
    }
  }, [isLoading, activeEntities, updateCommandDefinition]);

  return null;
}
